/**
 *  @class
 *  @function ThemeCart
 */
if (!customElements.get('theme-cart')) {
  class ThemeCart extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.setupEventListeners();
    }
    onFormChange(event) {
      const target = event.target;
      const idx =
        target?.dataset?.index ||
        target?.closest?.("[data-index]")?.dataset?.index;
      if (!idx) return;
      this.updateQuantity(idx, target.value);
    }
    onNotesChange() {
      this.saveNotes();
    }
    saveNotes() {
      fetch(`${theme.routes.cart_update_url}.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
        body: JSON.stringify({
          note: this.notes.value,
        }),
      });
    }
    autoReestimateShipping() {
      const host = document.querySelector("#cart-shipping-estimator");
      const se =
        host?.querySelector("shipping-estimator") ||
        document.querySelector("shipping-estimator");
      if (!se) return;

      // Detect inputs
      const countryEl = se.querySelector(
        'select[name="shipping-estimator[country]"]'
      );
      const zipEl = se.querySelector('input[name="shipping-estimator[zip]"]');
      const country = countryEl?.value?.trim();
      const zip = zipEl?.value?.trim();
      if (!country || !zip) return; // require minimal info

      clearTimeout(this._reestimateTimer);
      this._reestimateTimer = setTimeout(() => {
        try {
          if (typeof se.estimateShipping === "function") {
            se.estimateShipping();
            return;
          }
          const btn = se.querySelector(
            'button[type="button"], button[name="estimate"]'
          );
          if (btn) {
            btn.click();
            return;
          }
          const form = se.closest("form") || se.querySelector("form");
          if (form)
            form.dispatchEvent(
              new Event("submit", { bubbles: true, cancelable: true })
            );
        } catch (e) {
          /* no-op */
        }
      }, 220);
    }

    captureEstimatorState() {
      const host = document.querySelector("#cart-shipping-estimator");
      const se =
        host?.querySelector("shipping-estimator") ||
        document.querySelector("shipping-estimator");
      if (!se) return null;

      const pick = (sel) => se.querySelector(sel);
      const getVal = (el) =>
        el && typeof el.value === "string" ? el.value.trim() : "";

      const countryEl = pick('select[name="shipping-estimator[country]"]');
      const provinceEl = pick('select[name="shipping-estimator[province]"]');
      const zipEl = pick('input[name="shipping-estimator[zip]"]');

      return {
        has: !!(countryEl || zipEl),
        country: getVal(countryEl),
        province: getVal(provinceEl),
        zip: getVal(zipEl),
      };
    }

    restoreEstimatorStateAndReestimate() {
      const state = this._estimatorState;
      const host = document.querySelector("#cart-shipping-estimator");
      const se =
        host?.querySelector("shipping-estimator") ||
        document.querySelector("shipping-estimator");
      if (!se) return;

      if (!state || !state.has) {
        // no prior input -> best-effort auto
        this.autoReestimateShipping?.();
        return;
      }

      const pick = (sel) => se.querySelector(sel);
      const countryEl = pick('select[name="shipping-estimator[country]"]');

      if (countryEl && state.country) {
        countryEl.value = state.country;
        countryEl.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // helper to (re)apply province safely after options are populated
      const applyProvince = () => {
        const p = se.querySelector('select[name="shipping-estimator[province]"]');
        if (p && state.province) {
          const ok = Array.from(p.options || []).some(o => o.value === state.province || o.text === state.province);
          if (ok) {
            p.value = state.province;
            p.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      };

      // helper to (re)apply zip and make it sticky across cloned nodes
      const applyZip = () => {
        const z = se.querySelector('input[name="shipping-estimator[zip]"]');
        if (!z) return;
        const v = state.zip || '';
        if (document.activeElement !== z && z.value !== v) { z.value = v; }
        if (z.getAttribute('value') !== v) { try { z.setAttribute('value', v); } catch(e){} }
        // reinforce a few frames to beat late DOM swaps
        let i = 0;
        const tick = () => {
          i++;
          const z2 = se.querySelector('input[name="shipping-estimator[zip]"]');
          if (z2) {
            if (document.activeElement !== z2 && z2.value !== v) { z2.value = v; }
            if (z2.getAttribute('value') !== v) { try { z2.setAttribute('value', v); } catch(e){} }
          }
          if (i < 24) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      };

      // staged applies to avoid flicker
      setTimeout(() => {
        applyProvince();
        applyZip();
      }, 140);
      setTimeout(() => {
        applyProvince();
        applyZip();
      }, 320);

      // trigger auto re-estimate once inputs are likely stable
      setTimeout(() => { this.autoReestimateShipping?.(); }, 360);
    }
    setupEventListeners() {
      this.form = this.querySelector("#cart-form");
      this.notes = this.querySelector("#CartSpecialInstructions");

      this.removeProductEvent();

      // Single debounced change handler instance
      if (!this._debouncedOnFormChange) {
        this._debouncedOnFormChange = debounce((event) => {
          this.onFormChange(event);
        }, 300);
      }

      // Rebind cart:refresh
      document.addEventListener("cart:refresh", (event) => {
        this.refresh();
      });

      // Prevent duplicate listeners across re-renders
      if (this._changeHandler && this.form) {
        this.form.removeEventListener("change", this._changeHandler);
      }
      if (this._notesHandler && this.notes) {
        this.notes.removeEventListener("change", this._notesHandler);
      }
      if (this._onCartUpdated) {
        document.removeEventListener("cart:updated", this._onCartUpdated);
      }

      this._notesHandler = this.onNotesChange.bind(this);
      this.notes?.addEventListener("change", this._notesHandler);

      this._changeHandler = this._debouncedOnFormChange;
      this.form?.addEventListener("change", this._changeHandler);

      this._onCartUpdated = this.autoReestimateShipping.bind(this);
      document.addEventListener("cart:updated", this._onCartUpdated);
    }
    getSectionsToRender() {
      return [
        // Replace ONLY precise sub-areas; do NOT touch the parent that wraps the estimator
        { id: "Cart", section: "main-cart", selector: ".thb-cart-form--items" },
        { id: "Cart", section: "main-cart", selector: ".thb-cart-form--count" },
        {
          id: "Cart",
          section: "main-cart",
          selector: ".thb-cart-form--collaterals-inner",
        },

        // Header bubble
        {
          id: "cart-drawer-toggle",
          section: "cart-bubble",
          selector: ".thb-secondary-area--item-count",
        },
        {
          id: "cart-drawer-toggle",
          section: "cart-bubble",
          selector: ".thb-secondary-area--item-amount",
        },

        // Update the estimator container LAST
        {
          id: "Cart",
          section: "main-cart",
          selector: "#cart-shipping-estimator",
        },
      ];
    }
    displayErrors(line, message) {
      const lineItemError =
        document.getElementById(`Line-item-error-${line}`) ||
        document.getElementById(`CartDrawer-LineItemError-${line}`);
      if (lineItemError) {
        lineItemError.removeAttribute("hidden");
        lineItemError.querySelector(".cart-item__error-text").innerHTML =
          message;
        this.form
          .querySelector(`#CartItem-${line}`)
          .classList.remove("loading");

        this.setupEventListeners();
      }
    }
    getSectionInnerHTML(html, selector) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const node = doc.querySelector(selector);
      return node ? node.innerHTML : null;
    }
    updateQuantity(line, quantity) {
      this.form.classList.add("cart-disabled");
      this._estimatorState = this.captureEstimatorState();
      if (line) {
        this.form.querySelector(`#CartItem-${line}`)?.classList.add("loading");
      }

      const uniqueSections = [
        ...new Set(
          this.getSectionsToRender().map((section) => section.section)
        ),
      ];
      const body = JSON.stringify({
        line,
        quantity,
        sections: uniqueSections,
        sections_url: window.location.pathname,
      });
      dispatchCustomEvent("line-item:change:start", {
        quantity: quantity,
      });
      fetch(`${theme.routes.cart_change_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
        ...{
          body,
        },
      })
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          if (parsedState.errors) {
            this.displayErrors(line, parsedState.errors);
            this.form.classList.remove("cart-disabled");
            return;
          }

          this.renderContents(parsedState, line, false);

          // If the returned main-cart HTML indicates empty state, swap the wrapper once
          try {
            const mainCartHTML =
              parsedState.sections && parsedState.sections["main-cart"];
            if (mainCartHTML) {
              const doc = new DOMParser().parseFromString(
                mainCartHTML,
                "text/html"
              );
              const willBeEmpty = !!doc.querySelector(".thb-cart-form--empty");
              if (willBeEmpty) {
                const host = document.getElementById("Cart");
                const wrapperHTML = new DOMParser()
                  .parseFromString(mainCartHTML, "text/html")
                  .querySelector(".thb-cart-form")?.outerHTML;
                if (host && wrapperHTML) {
                  host.innerHTML = wrapperHTML;
                  // Rebind listeners and stop further processing
                  this.setupEventListeners();
                  this.form.classList.remove("cart-disabled");
                  return;
                }
              }
            }
          } catch (e) {
            /* no-op */
          }

          try {
            dispatchCustomEvent("cart:updated", {
              quantity,
              cart: parsedState,
            });
          } catch (e) {}

          this.form.classList.remove("cart-disabled");

          dispatchCustomEvent("line-item:change:end", {
            quantity: quantity,
            cart: parsedState,
          });
        });
    }
    refresh() {
      this.form?.classList.add("cart-disabled");

      const sectionHandles = [
        ...new Set(this.getSectionsToRender().map((s) => s.section)),
      ];
      const sectionsParam = encodeURIComponent(sectionHandles.join(","));

      fetch(`${window.location.pathname}/?sections=${sectionsParam}`)
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);

          if (parsedState.errors) {
            this.form?.classList.remove("cart-disabled");
            return;
          }

          this.renderContents(parsedState, false, true);

          // Empty-state switch on full refresh as well
          try {
            const mainCartHTML =
              parsedState["main-cart"] ||
              (parsedState.sections && parsedState.sections["main-cart"]);
            if (mainCartHTML) {
              const doc = new DOMParser().parseFromString(
                mainCartHTML,
                "text/html"
              );
              const willBeEmpty = !!doc.querySelector(".thb-cart-form--empty");
              if (willBeEmpty) {
                const host = document.getElementById("Cart");
                const wrapperHTML = new DOMParser()
                  .parseFromString(mainCartHTML, "text/html")
                  .querySelector(".thb-cart-form")?.outerHTML;
                if (host && wrapperHTML) {
                  host.innerHTML = wrapperHTML;
                  this.setupEventListeners();
                }
              }
            }
          } catch (e) {
            /* no-op */
          }

          try {
            dispatchCustomEvent("cart:updated", { cart: parsedState });
          } catch (e) {}

          this.form?.classList.remove("cart-disabled");
        });
    }
    removeProductEvent() {
      if (this._removeHandler && this.form) {
        this.form.removeEventListener("click", this._removeHandler);
      }
      this._removeHandler = (event) => {
        const trigger = event.target.closest(
          '.remove, [data-remove], [data-cart-remove], [data-action="remove"], [aria-label="Remove"], a[href*="/cart/change"], button[name="remove"]'
        );
        if (!trigger) return;

        const containerWithIndex = trigger.closest("[data-index], [data-line]");
        let line =
          trigger.dataset.index ||
          trigger.dataset.line ||
          containerWithIndex?.dataset?.index ||
          containerWithIndex?.dataset?.line;

        if (!line) {
          const row = trigger.closest('[id^="CartItem-"]');
          const m = row?.id?.match(/CartItem-(\d+)/);
          if (m) line = m[1];
        }

        if (!line) return;

        event.preventDefault();
        this.updateQuantity(line, "0");
      };
      this.form?.addEventListener("click", this._removeHandler);
    }
    renderContents(parsedState, line, refresh) {
      this.getSectionsToRender().forEach((section) => {
        const host = document.getElementById(section.id);
        if (!host) return;
        const elementToReplace = host.querySelector(section.selector) || host;
        if (!elementToReplace) return;

        if (refresh) {
          if (parsedState[section.section]) {
            const newHTML = this.getSectionInnerHTML(
              parsedState[section.section],
              section.selector
            );
            if (newHTML !== null) {
              elementToReplace.innerHTML = newHTML;
            }
          }
        } else {
          const newHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
          if (newHTML !== null) {
            elementToReplace.innerHTML = newHTML;
          }
        }
      });

      // Rebind listeners once after DOM swaps
      this.removeProductEvent();
      this.setupEventListeners();

      this.restoreEstimatorStateAndReestimate();

      if (line && this.form?.querySelector(`#CartItem-${line}`)) {
        this.form
          .querySelector(`#CartItem-${line}`)
          .classList.remove("loading");
      }
    }
  }
  customElements.define("theme-cart", ThemeCart);

  // ---- Global Shipping Estimator Auto-Estimate helper (shared by cart page & drawer) ----
  (function () {
    if (!window.__seAutoEstimate) {
      window.__seAutoEstimate = function (scope) {
        try {
          if (window.__seEstimating) return; // lock: avoid recursive triggering
          window.__seEstimating = true;

          var root = scope || document;
          var se =
            root.querySelector("#cart-shipping-estimator shipping-estimator") ||
            root.querySelector("shipping-estimator");
          if (!se) {
            window.__seEstimating = false;
            return;
          }
          var country = se.querySelector(
            'select[name="shipping-estimator[country]"]'
          );
          var zip = se.querySelector('input[name="shipping-estimator[zip]"]');
          if (
            !(
              country &&
              country.value &&
              zip &&
              String(zip.value).trim().length
            )
          ) {
            window.__seEstimating = false;
            return;
          }

          var done = function () {
            setTimeout(function () {
              window.__seEstimating = false;
            }, 1200);
          };

          try {
            if (typeof se.estimateShipping === "function") {
              se.estimateShipping();
              done();
              return;
            }
          } catch (e) {
            /* fallthrough */
          }
          var btn = se.querySelector(
            'button[type="button"], button[name="estimate"]'
          );
          if (btn) {
            btn.click();
            done();
            return;
          }
          window.__seEstimating = false;
        } catch (e) {
          window.__seEstimating = false;
        }
      };
    }
    if (!window.__seAutoEstimateBound) {
      window.__seAutoEstimateBound = true;
      document.addEventListener("cart:updated", function () {
        try {
          window.__seAutoEstimate && window.__seAutoEstimate(document);
        } catch (e) {}
      });
    }
  })();
}
